import React, { PureComponent } from 'react';
import {
  SafeAreaView,
  StyleSheet, View, Text, Image,
} from 'react-native';
import { connect } from 'react-redux';
import { UserProfile } from 'fitbeat.gym-core';
import { withTranslation, WithTranslation } from 'react-i18next';
import { defaultScale } from '../../../../utils/scaling';
import { getNextStep, navigateToNextStep } from '../../../actions';
import { IAppStore } from '../../../../HomeWorkouts';
import { GoogleAnalytics } from '../../../../services/google-analytics';
import Theme from '../../../../theme';
import { UserDataConsumer } from '../../../../utils';
import GarminConnectImage from '../../../../assets/icons/garmin-connect.png';
import { IConnectedAppOnboardingProps, navigateToNextOnboardingStep } from '../ConnectedAppEntryPointScreen';
import { LoadingModal } from '../../../../ui';
import { connectToApp, handleConnectionToGarmin } from '../utils';
import { setUserGarminWorkoutImportConfig } from '../../../oauth/actions';
import { IOauthParams } from '../../../../../../models/src/links/deep-link-parameters';
import { deepLinkParamsSelector, garminIsEnabledForUserSelector } from '../../../../auth/auth-selectors';
import { showMessage } from '../../../../ui/components/Toast';
import { clearDeepLinkParams } from '../../../../utils/notifications/actions';
import ConnectedAppsManagerSingleton, { IAppForOnboarding } from '../../../../ConnectedApps';
import { createUserServiceClient } from '../../../../utils/apiClientFactory';
import FooterButtons from '../shared/FooterButtons';
import MultiStyleText from '../../../../ui/components/MultiStyleText';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: defaultScale(16),
    backgroundColor: Theme.WHITE,
  },
  headerContainer: {
    alignItems: 'center',
    marginTop: '10%',
    textAlign: 'center',
    flex: 1,
  },
  heading: {
    textAlign: 'center',
  },
  garminConnectIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: defaultScale(20),
  },
  subHeader: {
    marginTop: defaultScale(30),
    color: Theme.GREY_DARK_MEDIUM,
    marginBottom: defaultScale(10),
    paddingHorizontal: defaultScale(20),
    textAlign: 'center',
  },
  footerButtons: {
    bottom: 0,
    paddingHorizontal: defaultScale(16),
    width: '100%',
  },
  actionSheetContainer: {
    width: '100%',
    alignItems: 'center',
  },
  button: {
    marginTop: defaultScale(10),
    width: '100%',
  },
});

interface IState {
  isLoading: boolean;
}

interface IProps extends IConnectedAppOnboardingProps, WithTranslation {
  user: {
    userProfileService: any;
    userProfile: UserProfile;
  };
  garminAuth: IOauthParams;
  garminIsEnabledForUser?: boolean;
  settingUserGarminWorkoutImportConfig: (data: IOauthParams) => void;
  clearDeepLinks: (keys: string[]) => void;
}

class EnableGarminConnect extends PureComponent<IProps, IState> {
  private readonly googleAnalytics: GoogleAnalytics;

  public constructor(props: IProps) {
    super(props);
    this.googleAnalytics = new GoogleAnalytics();
    this.state = {
      isLoading: false,
    };
  }

  public async componentDidUpdate(prevProps: IProps) {
    if (this.verifyGarminAuth()) {
      await handleConnectionToGarmin({
        ...this.props,
        userProfile: this.props.user.userProfile,
      });
      if (this.props.garminIsEnabledForUser === true) {
        this.props.navigation.navigate('GarminConnectSyncEnabled');
      } else if (this.props.garminIsEnabledForUser === false) {
        this.setState({ isLoading: false });
      }
    }
  }

  public render() {
    const { t } = this.props;
    const appForOnboarding: IAppForOnboarding = this.props.navigation.getParam('appForOnboarding');

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.headerContainer}>
          <MultiStyleText
            templateString={t('enableApp')}
            styleKeys={[
              {
                keyName: 'connectedApp',
                keyValue: appForOnboarding.app,
                style: Theme.textStyle.heading,
              },
            ]}
            normalStyle={{ ...Theme.textStyle.heading, ...styles.heading }}
          />
          <View style={styles.garminConnectIcon}>
            <Image
              source={GarminConnectImage}
              style={{ width: defaultScale(100), height: defaultScale(100), marginTop: defaultScale(30) }}
            />
          </View>
          <Text style={[Theme.textStyle.bodyCopyReg, styles.subHeader]}>
            {t('enableGarminConnectDescription')}
          </Text>
        </View>
        <FooterButtons
          primaryButtonText={t('enable')}
          secondaryButtonText={t('maybeLater')}
          onPrimaryButtonPress={() => this.requestAuth()}
          onSecondaryButtonPress={() => this.proceedToNextStep()}
          isLoading={this.state.isLoading}
        />
      </SafeAreaView>
    );
  }

  private verifyGarminAuth = () => {
    if (this.props.garminAuth?.oauthToken !== 'null'
        && this.props.garminAuth?.oauthVerifier !== 'null'
    ) {
      return true;
    }
    showMessage('Garmin connection failed');
    return false;
  };

  private async requestAuth() {
    const appForOnboarding = this.props.navigation.getParam('appForOnboarding') as IAppForOnboarding;
    const app = ConnectedAppsManagerSingleton.getAppByName(appForOnboarding.app);
    if (!app) {
      showMessage(`App ${appForOnboarding.app} not found`);
      return;
    }
    const result = await connectToApp(app);

    if (result === 'loading') {
      this.setState({ isLoading: true });
    } else if (result === 'error') {
      showMessage(result);
    }
  }

  private proceedToNextStep = () => {
    navigateToNextOnboardingStep(
      this.props,
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
    this.googleAnalytics.logEventWithParamaters('sign_up_garmin_sync_enabled_proceed', { enabled: true });
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
  garminAuth: deepLinkParamsSelector(state).garminAuth,
  garminIsEnabledForUser: garminIsEnabledForUserSelector(state),
});

const mapDispatchToProps = {
  settingUserGarminWorkoutImportConfig: (data: IOauthParams) => (dispatch) => {
    const userServiceClient = createUserServiceClient();
    dispatch(setUserGarminWorkoutImportConfig(userServiceClient, data));
  },
  getNextStep,
  navigateToNextStep,
  clearDeepLinks: clearDeepLinkParams,
};

export default withTranslation(['auth', 'settings'])(
  connect(mapStateToProps, mapDispatchToProps)(UserDataConsumer(EnableGarminConnect)),
);
